import { faCalendarDay } from "@fortawesome/pro-regular-svg-icons/faCalendarDay";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { Box, Typography, IconButton, Paper, styled } from "@mui/material";
import { InputProps as StandardInputProps } from "@mui/material/Input";
import {
  DatePicker as MUIDatePicker,
  DatePickerProps as MUIDatePickerProps,
} from "@mui/x-date-pickers/DatePicker";
import { format as formatDateFns } from "date-fns";
import {
  ReactElement,
  ReactNode,
  forwardRef,
  useState,
  useCallback,
  HTMLAttributes,
  Ref,
  useMemo,
  useEffect,
  useId,
} from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";

const DEFAULT_DISPLAY_FORMAT = "dd MMM, yyyy";

type DatePickerOnChangeContext = Parameters<NonNullable<MUIDatePickerProps["onChange"]>>[1];

export type SrDatePickerProps = Omit<MUIDatePickerProps, "value"> & {
  id?: string;
  value?: Date | string | null;
  placeholder?: string;
  hasClearButton?: boolean;
  error?: boolean;
  children?: ReactNode;
  ariaLabelledBy?: string;
  clearDateAriaLabel?: string;
  openDatePickerAriaLabel?: string;
  // The date-fns pattern the field shows its value in.
  displayFormat?: string;
};

export const SrDatePicker = ({
  id,
  value,
  placeholder,
  hasClearButton,
  error,
  onChange,
  disabled,
  readOnly,
  ariaLabelledBy,
  clearDateAriaLabel = "Clear date",
  openDatePickerAriaLabel = "Open date picker",
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  ...props
}: SrDatePickerProps): ReactElement => {
  const [open, setOpen] = useState(false);

  // Convert value to Date object or null
  const dateValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const date = new Date(value);
    return !isNaN(date.getTime()) ? date : null;
  }, [value]);

  const [newValue, setNewValue] = useState<Date | null>(dateValue);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setNewValue(dateValue);
  }, [dateValue]);

  const handleRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setAnchorEl(node);
    }
  }, []);

  const onOpen = () => {
    setOpen(true);
  };
  const onClose = () => setOpen(false);

  const handleChange = (newDate: Date | null, context: DatePickerOnChangeContext) => {
    setNewValue(newDate);
    if (onChange) {
      onChange(newDate, context);
    }
  };

  const handleClear = () => {
    handleChange(null, { source: "field", validationError: null });
  };

  return (
    <Box>
      <MUIDatePicker
        {...props}
        disabled={disabled}
        readOnly={readOnly}
        value={newValue}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        onChange={handleChange}
        slots={{
          field: SrCustomField,
        }}
        slotProps={{
          field: {
            // @ts-expect-error Custom type
            inputContainerRef: handleRef,
            id,
            placeholder,
            value: newValue,
            hasClearButton,
            ariaLabelledBy,
            clearDateAriaLabel,
            openDatePickerAriaLabel,
            displayFormat,
            onOpenPicker: onOpen,
            onClear: handleClear,
            error,
            disabled,
            readOnly,
          },
          popper: {
            placement: "bottom-end",
            anchorEl,
          },
        }}
      />
    </Box>
  );
};

const SrDatePickerInputBox = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  "&.Mui-readOnly": {
    cursor: "default",
  },
  "&.Mui-focused": {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: "not-allowed",
    opacity: 0.7,
  },
  "&.Mui-error": {
    color: "warning.main",
    borderColor: theme.palette.error.main,
    borderWidth: 2,
    "&:hover": {
      borderColor: theme.palette.error.dark,
    },
  },
}));

interface SrCustomFieldPropsFromPicker {
  id?: string;
  inputContainerRef?: (node: HTMLElement | null) => void;
  placeholder?: string;
  value?: Date | null;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  focused?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  ownerState: unknown;
  selectedSections?: number | null | undefined;
  onSelectedSectionsChange?: (newSelectedSections: number | null | string) => void;
  format?: string;
  formatDensity?: "dense" | "normal" | string;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  hasClearButton?: boolean;
  ariaLabelledBy?: string;
  clearDateAriaLabel?: string;
  openDatePickerAriaLabel?: string;
  displayFormat?: string;
  onOpenPicker?: () => void;
  onClear?: () => void;
  InputProps?: Partial<StandardInputProps>;
  slotProps?: unknown;
}

type CombinedSrCustomFieldProps = SrCustomFieldPropsFromPicker &
  Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "value">;

const SrCustomField = forwardRef<HTMLDivElement, CombinedSrCustomFieldProps>((props, ref) => {
  const {
    id,
    inputContainerRef,
    placeholder,
    value,
    disabled,
    readOnly,
    error,
    focused,
    inputRef,
    ownerState,
    hasClearButton,
    ariaLabelledBy,
    clearDateAriaLabel,
    openDatePickerAriaLabel,
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    onOpenPicker,
    onClear,
    onClick,
    InputProps: _InputProps,
    selectedSections: _selectedSections,
    onSelectedSectionsChange: _onSelectedSectionsChange,
    format: _format,
    formatDensity: _formatDensity,
    disablePast: _disablePast,
    disableFuture: _disableFuture,
    minDate: _minDate,
    maxDate: _maxDate,
    slotProps: _slotProps,
    ...other
  } = props;

  const { onOpen } = (ownerState || {}) as {
    onOpen?: () => void;
  };

  const displayValue = value ? formatDateFns(value, displayFormat) : placeholder;
  const generatedDisplayValueId = useId();
  const displayValueId = id ? `${id}-display-value` : generatedDisplayValueId;

  const handleOpenPicker = (event: React.MouseEvent<HTMLElement>) => {
    if (onClick) onClick(event as React.MouseEvent<HTMLDivElement>);
    if (!readOnly && !disabled) {
      (onOpenPicker ?? onOpen)?.();
    }
  };

  const handleClearDate = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!readOnly && !disabled && onClear) {
      onClear();
    }
  };

  const hasError = !!error;

  const combinedRef = useCallback(
    (node: HTMLElement | null) => {
      const divNode = node as HTMLDivElement | null;

      if (ref && typeof ref === "function") ref(divNode);
      else if (ref && "current" in ref) ref.current = divNode;
      if (inputContainerRef) inputContainerRef(divNode);
    },
    [ref, inputContainerRef],
  );

  // Determine text color based on value and error state
  let textColor: string;
  if (error) textColor = "warning.dark";
  else if (value) textColor = "text.primary";
  else textColor = "text.secondary";

  return (
    <Box id={id} ref={combinedRef} {...other}>
      <SrDatePickerInputBox
        onClick={handleOpenPicker}
        // A read-only field opens nothing (`handleOpenPicker` refuses), so it must not announce
        // itself as a dialog opener or sit in the tab order — otherwise a keyboard user reaches a
        // control that promises a picker and does nothing. `disabled` keeps its own semantics.
        role={readOnly ? undefined : "button"}
        aria-labelledby={ariaLabelledBy ? `${ariaLabelledBy} ${displayValueId}` : undefined}
        aria-label={ariaLabelledBy ? undefined : displayValue}
        aria-haspopup={readOnly ? undefined : "dialog"}
        tabIndex={disabled || readOnly ? -1 : 0}
        elevation={0}
        className={`${disabled ? "Mui-disabled" : ""} ${readOnly ? "Mui-readOnly" : ""} ${
          hasError ? "Mui-error" : ""
        } ${focused ? "Mui-focused" : ""}`}
      >
        {/* Hidden input for accessibility and focus management */}
        <input
          ref={inputRef}
          aria-hidden="true"
          aria-invalid={error}
          tabIndex={-1}
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 1,
            height: 1,
            left: -9999,
          }}
          value={value ? value.toISOString() : ""}
          readOnly
          disabled={disabled}
        />

        {/* Formatted date or "Pick a date" message */}
        <Typography
          id={displayValueId}
          variant="body1"
          sx={{
            color: textColor,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayValue}
        </Typography>

        {/* Clear button */}
        {hasClearButton && value && !readOnly && !disabled && (
          <IconButton
            aria-label={clearDateAriaLabel}
            onClick={handleClearDate}
            size="small"
            sx={{ ml: 1 }}
          >
            <FaIcon icon={faXmark} />
          </IconButton>
        )}

        {/* Calendar icon */}
        {!readOnly && (
          <IconButton
            aria-label={openDatePickerAriaLabel}
            onClick={handleOpenPicker}
            size="small"
            sx={{ ml: 1, color: error ? "warning.dark" : "initial" }}
            disabled={disabled}
          >
            <FaIcon icon={faCalendarDay} />
          </IconButton>
        )}
      </SrDatePickerInputBox>
    </Box>
  );
});
